import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { COUNTRIES } from '../../../constants/countries';

const positionRules = {
  required: 'Required',
  max: { value: 30, message: 'Max 30' },
  min: { value: -30, message: 'Min -30' }
};

const dimensionRules = {
  required: 'Required',
  max: { value: 30, message: 'Max 30' },
  min: { value: 0.1, message: 'Min 0.1' }
};

const widthRules = {
  required: 'Required',
  max: { value: 30, message: 'Max 30' },
  min: { value: 0, message: 'Min 0' }
};

const defaultValues = {
  country: '',
  bankName: '',
  templateName: '',
  templateHeight: '',
  templateWidth: '',
  beneficiaryLeft: '', beneficiaryTop: '', beneficiaryWidth: '',
  beneficiary2Left: '', beneficiary2Top: '',
  dateLeft: '', dateTop: '', dateWidth: '',
  amountLeft: '', amountTop: '', amountWidth: '',
  amountInWords1Left: '', amountInWords1Top: '', amountInWordsWidth: '',
  amountInWords2Left: '', amountInWords2Top: '', amount2Width: '',
  accountPayeeLeft: '', accountPayeeTop: '',
  remarksLeft: '', remarksTop: '', remarksWidth: ''
};

const CoordinateField = ({ control, name, label, rules, errors }) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field }) => (
      <TextField
        {...field}
        label={label}
        type="number"
        size="small"
        inputProps={{ step: '0.01' }}
        error={!!errors[name]}
        helperText={errors[name]?.message}
        sx={{ flex: 1, minWidth: 90 }}
      />
    )}
  />
);

const SectionHeader = ({ title, color }) => (
  <Typography
    variant="subtitle2"
    sx={{ fontWeight: 700, color: color || '#334155', mt: 2, mb: 1 }}
  >
    {title}
  </Typography>
);

const TemplateLibraryFormModal = ({ open, onClose, onSave, template, loading }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({ defaultValues });

  useEffect(() => {
    if (open) {
      if (template) {
        reset({
          country: template.country || '',
          bankName: template.bankName || '',
          templateName: template.templateName || '',
          templateHeight: template.templateHeight ?? '',
          templateWidth: template.templateWidth ?? '',
          beneficiaryLeft: template.beneficiaryLeft ?? '',
          beneficiaryTop: template.beneficiaryTop ?? '',
          beneficiaryWidth: template.beneficiaryWidth ?? '',
          beneficiary2Left: template.beneficiary2Left ?? '',
          beneficiary2Top: template.beneficiary2Top ?? '',
          dateLeft: template.dateLeft ?? '',
          dateTop: template.dateTop ?? '',
          dateWidth: template.dateWidth ?? '',
          amountLeft: template.amountLeft ?? '',
          amountTop: template.amountTop ?? '',
          amountWidth: template.amountWidth ?? '',
          amountInWords1Left: template.amountInWords1Left ?? '',
          amountInWords1Top: template.amountInWords1Top ?? '',
          amountInWordsWidth: template.amountInWordsWidth ?? '',
          amountInWords2Left: template.amountInWords2Left ?? '',
          amountInWords2Top: template.amountInWords2Top ?? '',
          amount2Width: template.amount2Width ?? '',
          accountPayeeLeft: template.accountPayeeLeft ?? '',
          accountPayeeTop: template.accountPayeeTop ?? '',
          remarksLeft: template.remarksLeft ?? '',
          remarksTop: template.remarksTop ?? '',
          remarksWidth: template.remarksWidth ?? ''
        });
      } else {
        reset(defaultValues);
      }
    }
  }, [open, template, reset]);

  const onSubmit = (data) => {
    const payload = {
      Country: data.country,
      BankName: data.bankName,
      TemplateName: data.templateName,
      TemplateHeight: Number(data.templateHeight),
      TemplateWidth: Number(data.templateWidth),
      BeneficiaryLeft: Number(data.beneficiaryLeft) || 0,
      BeneficiaryTop: Number(data.beneficiaryTop) || 0,
      BeneficiaryWidth: Number(data.beneficiaryWidth) || 0,
      Beneficiary2Left: Number(data.beneficiary2Left) || 0,
      Beneficiary2Top: Number(data.beneficiary2Top) || 0,
      DateLeft: Number(data.dateLeft) || 0,
      DateTop: Number(data.dateTop) || 0,
      DateWidth: Number(data.dateWidth) || 0,
      AmountLeft: Number(data.amountLeft) || 0,
      AmountTop: Number(data.amountTop) || 0,
      AmountWidth: Number(data.amountWidth) || 0,
      AmountInWords1Left: Number(data.amountInWords1Left) || 0,
      AmountInWords1Top: Number(data.amountInWords1Top) || 0,
      AmountInWordsWidth: Number(data.amountInWordsWidth) || 0,
      AmountInWords2Left: Number(data.amountInWords2Left) || 0,
      AmountInWords2Top: Number(data.amountInWords2Top) || 0,
      Amount2Width: Number(data.amount2Width) || 0,
      AccountPayeeLeft: Number(data.accountPayeeLeft) || 0,
      AccountPayeeTop: Number(data.accountPayeeTop) || 0,
      RemarksLeft: Number(data.remarksLeft) || 0,
      RemarksTop: Number(data.remarksTop) || 0,
      RemarksWidth: Number(data.remarksWidth) || 0
    };
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {template ? 'Edit Library Template' : 'Add Library Template'}
      </DialogTitle>
      <DialogContent dividers>
        <form id="template-library-form" onSubmit={handleSubmit(onSubmit)}>
          {/* Template Info */}
          <SectionHeader title="Template Information" color="#14b8a6" />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Controller
              name="country"
              control={control}
              rules={{ required: 'Country is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Country"
                  size="small"
                  error={!!errors.country}
                  helperText={errors.country?.message}
                  sx={{ flex: 1 }}
                >
                  {COUNTRIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="bankName"
              control={control}
              rules={{ required: 'Bank name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bank Name"
                  size="small"
                  placeholder="e.g. Mashreq Bank"
                  error={!!errors.bankName}
                  helperText={errors.bankName?.message}
                  sx={{ flex: 1 }}
                />
              )}
            />
            <Controller
              name="templateName"
              control={control}
              rules={{ required: 'Template name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Template Name"
                  size="small"
                  placeholder="e.g. Neo Biz"
                  error={!!errors.templateName}
                  helperText={errors.templateName?.message}
                  sx={{ flex: 1 }}
                />
              )}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Cheque Dimensions */}
          <SectionHeader title="Cheque Dimensions (cm)" color="#2563eb" />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <CoordinateField control={control} name="templateHeight" label="Height (cm)" rules={dimensionRules} errors={errors} />
            <CoordinateField control={control} name="templateWidth" label="Width (cm)" rules={dimensionRules} errors={errors} />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Beneficiary */}
          <SectionHeader title="Beneficiary Position" color="#2563eb" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <CoordinateField control={control} name="beneficiaryLeft" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="beneficiaryTop" label="Top" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="beneficiaryWidth" label="Width" rules={widthRules} errors={errors} />
          </Box>

          {/* Beneficiary Line 2 */}
          <SectionHeader title="Beneficiary Line 2" color="#3b82f6" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <CoordinateField control={control} name="beneficiary2Left" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="beneficiary2Top" label="Top" rules={positionRules} errors={errors} />
          </Box>

          {/* Date */}
          <SectionHeader title="Date Position" color="#16a34a" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <CoordinateField control={control} name="dateLeft" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="dateTop" label="Top" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="dateWidth" label="Width" rules={widthRules} errors={errors} />
          </Box>

          {/* Amount */}
          <SectionHeader title="Amount Position" color="#ea580c" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <CoordinateField control={control} name="amountLeft" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="amountTop" label="Top" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="amountWidth" label="Width" rules={widthRules} errors={errors} />
          </Box>

          {/* Amount in Words Line 1 */}
          <SectionHeader title="Amount in Words - Line 1" color="#7c3aed" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <CoordinateField control={control} name="amountInWords1Left" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="amountInWords1Top" label="Top" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="amountInWordsWidth" label="Width" rules={widthRules} errors={errors} />
          </Box>

          {/* Amount in Words Line 2 */}
          <SectionHeader title="Amount in Words - Line 2" color="#9333ea" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <CoordinateField control={control} name="amountInWords2Left" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="amountInWords2Top" label="Top" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="amount2Width" label="Width" rules={widthRules} errors={errors} />
          </Box>

          {/* Account Payee */}
          <SectionHeader title="A/C Payee Position" color="#0891b2" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <CoordinateField control={control} name="accountPayeeLeft" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="accountPayeeTop" label="Top" rules={positionRules} errors={errors} />
          </Box>

          {/* Remarks */}
          <SectionHeader title="Remarks Position" color="#64748b" />
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <CoordinateField control={control} name="remarksLeft" label="Left" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="remarksTop" label="Top" rules={positionRules} errors={errors} />
            <CoordinateField control={control} name="remarksWidth" label="Width" rules={widthRules} errors={errors} />
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          type="submit"
          form="template-library-form"
          disabled={loading}
          sx={{ backgroundColor: '#14b8a6', '&:hover': { backgroundColor: '#0d9488' } }}
        >
          {template ? 'Update' : 'Add Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateLibraryFormModal;
